import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

/** The radius is a token — a shell rounds its buttons via `--radius`, never by overriding `rounded-*`. */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[color,background-color,border-color,box-shadow,transform] duration-150 motion-safe:active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-error-700',
        outline: 'border border-input bg-card hover:border-foreground/20 hover:bg-muted/50 hover:text-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        // The design system's "dark" button: strong forward actions that shouldn't
        // compete with a screen's one lime action (e.g. Next inside an exam).
        ink: 'bg-foreground text-background hover:bg-foreground/90',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // Ink with a quiet underline: lime text is unreadable on white.
        link: 'text-foreground underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground',
      },
      // Size changes height and padding only — never the radius.
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3.5',
        // Dense 32px: panel headers, table toolbars, empty states.
        xs: 'h-8 px-3 text-[13px]',
        lg: 'h-11 px-[18px]',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Opt out of the active-press scale (for contexts where motion distracts). */
  static?: boolean;
  icon?: React.ReactNode; // Accept but ignore for compatibility
  leftIcon?: React.ReactNode; // Accept but ignore for compatibility
  rightIcon?: React.ReactNode; // Accept but ignore for compatibility
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, static: isStatic = false, icon, leftIcon, rightIcon, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), isStatic && 'motion-safe:active:scale-100')}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
